import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  Mail,
  DollarSign,
  TrendingUp,
  Shield,
  Wrench,
  Fuel,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Info,
  Check
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AuthGate from '@/components/AuthGate';

// TEMP: Auth bypass for testing Compare flow
// Set REACT_APP_DEV_AUTH_BYPASS=true in .env to enable
// Default is OFF (false) - auth gate works normally
const DEV_AUTH_BYPASS = process.env.REACT_APP_DEV_AUTH_BYPASS === 'true';

// Empty car template with input mode
const createEmptyCar = () => ({
  inputMode: 'manual', // 'link' or 'manual'
  year: '',
  make: '',
  model: '',
  condition: 'used',
  price: '',
  zip: '',
  mileage: '',
  listing_link: '',
  trim: '',
  vin: ''
});

const CompareCars = ({ isEmbedded = false, onContinue, onBack: onBackProp }) => {
  const navigate = useNavigate();
  const { isAuthenticated, getRemainingCompareReports, fetchEntitlements } = useAuth();
  const [showAuthGate, setShowAuthGate] = useState(false);
  
  // Scale animation state for hero visual
  const [scaleSettled, setScaleSettled] = useState(false);
  
  useEffect(() => {
    // Trigger scale animation after mount
    const scaleTimer = setTimeout(() => setScaleSettled(true), 100);
    return () => clearTimeout(scaleTimer);
  }, []);
  
  // Car inputs state - start with just Car 1
  const [cars, setCars] = useState([createEmptyCar()]);
  const [visibleCarCount, setVisibleCarCount] = useState(1);
  
  // Form state
  const [errors, setErrors] = useState({});
  
  // API state
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [notEnoughCarsError, setNotEnoughCarsError] = useState(false);
  const [results, setResults] = useState(null);
  const [reportsRemaining, setReportsRemaining] = useState(null);
  
  // Accordion state for explainer sections
  const [accordions, setAccordions] = useState({
    howWeRank: false,
    whatWeLookAt: false,
    whereDataComesFrom: false
  });

  // VIN decode state (per car index)
  const [decodingVin, setDecodingVin] = useState({});  // { carIndex: true/false }
  const [vinDecodeResults, setVinDecodeResults] = useState({});  // { carIndex: { success, error, ... } }
  const [lockedFields, setLockedFields] = useState({});  // { carIndex: { year: true, make: true, ... } }
  const [autoFilledFields, setAutoFilledFields] = useState({});  // { carIndex: { year: true, make: true, ... } } - tracks visual confirmation state

  const reportsInfo = getRemainingCompareReports();

  // Score breakdown segments with brand colors and icons
  const scoreSegments = [
    { id: 'price_fairness', label: 'Price fairness', points: 30, color: '#001F3F', icon: DollarSign },
    { id: 'value_retention', label: 'Value retention', points: 25, color: '#059669', icon: TrendingUp },
    { id: 'safety', label: 'Safety', points: 20, color: '#7C3AED', icon: Shield },
    { id: 'reliability_repair', label: 'Reliability and repair risk', points: 15, color: '#DC2626', icon: Wrench },
    { id: 'running_costs', label: 'Running costs', points: 10, color: '#3B82F6', icon: Fuel, sublabel: 'includes fuel economy when available' }
  ];

  // Category explanations
  const categoryExplanations = [
    { title: 'Price fairness', content: 'We compare the price you enter to a typical range for similar cars, same make, model, year range, trim when available, similar mileage, and your region when available. A car priced below typical can score higher. A car priced above typical can score lower.' },
    { title: 'Value retention', content: 'Some models depreciate faster than others. We factor in typical depreciation patterns so you can avoid options that tend to lose value quickly.' },
    { title: 'Safety', content: 'Where public safety information exists, we use government and independent results. We do not create our own crash ratings. We summarize what is published and link to sources when possible.' },
    { title: 'Reliability and repair risk', content: 'We estimate expected maintenance and repair burden using the best available data sources. When model level signals are weak or missing, we show lower confidence rather than pretending we know.' },
    { title: 'Running costs', content: 'We consider published fuel economy for the model family when available, because fuel costs can be a meaningful part of ownership.' }
  ];

  // Get confidence pill styles
  const getConfidenceStyles = (level) => {
    switch (level) {
      case 'High': return { backgroundColor: '#DCFCE7', color: '#166534' };
      case 'Medium': return { backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'Low': return { backgroundColor: '#FEE2E2', color: '#991B1B' };
      case 'Insufficient': return { backgroundColor: '#F3F4F6', color: '#6B7280' };
      default: return { backgroundColor: '#F3F4F6', color: '#374151' };
    }
  };

  // Update car field
  const updateCarField = (index, field, value) => {
    setCars(prev => {
      const newCars = [...prev];
      newCars[index] = { ...newCars[index], [field]: value };
      return newCars;
    });
    // Clear error for this field
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`car${index}_${field}`];
      return newErrors;
    });
    // If user manually edits an auto-filled field, remove its confirmed status
    if (['year', 'make', 'model', 'trim'].includes(field) && autoFilledFields[index]?.[field]) {
      setAutoFilledFields(prev => {
        const newAutoFilled = { ...prev };
        if (newAutoFilled[index]) {
          newAutoFilled[index] = { ...newAutoFilled[index] };
          delete newAutoFilled[index][field];
          // If no more auto-filled fields, remove the entry
          if (Object.keys(newAutoFilled[index]).length === 0) {
            delete newAutoFilled[index];
          }
        }
        return newAutoFilled;
      });
      // Also unlock the field
      setLockedFields(prev => {
        const newLocked = { ...prev };
        if (newLocked[index]) {
          newLocked[index] = { ...newLocked[index] };
          delete newLocked[index][field];
          if (Object.keys(newLocked[index]).length === 0) {
            delete newLocked[index];
          }
        }
        return newLocked;
      });
    }
  };

  // Unlock fields for manual editing (when user clicks "Edit")
  const unlockFields = (index) => {
    setLockedFields(prev => {
      const newLocked = { ...prev };
      delete newLocked[index];
      return newLocked;
    });
    // Clear auto-filled visual state
    setAutoFilledFields(prev => {
      const newAutoFilled = { ...prev };
      delete newAutoFilled[index];
      return newAutoFilled;
    });
    // Clear VIN decode result message
    setVinDecodeResults(prev => {
      const newResults = { ...prev };
      delete newResults[index];
      return newResults;
    });
  };

  // Decode VIN and auto-fill car fields
  const decodeVin = async (index) => {
    const vin = cars[index]?.vin?.trim();
    
    if (!vin) {
      setVinDecodeResults(prev => ({ 
        ...prev, 
        [index]: { 
          success: false, 
          error: 'Enter a VIN to auto-fill.' 
        } 
      }));
      return;
    }
    
    setDecodingVin(prev => ({ ...prev, [index]: true }));
    setVinDecodeResults(prev => {
      const newResults = { ...prev };
      delete newResults[index];
      return newResults;
    });
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/decode-vin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vin: vin })
      });
      
      const data = await response.json();
      
      setVinDecodeResults(prev => ({ ...prev, [index]: data }));
      
      if (data.success && data.decoded) {
        // Auto-fill the car fields
        const decoded = data.decoded;
        
        // Build fieldsToLock and fieldsAutoFilled BEFORE setCars
        const fieldsToLock = {};
        const fieldsAutoFilled = {};
        
        if (decoded.year) {
          fieldsToLock.year = true;
          fieldsAutoFilled.year = true;
        }
        if (decoded.make) {
          fieldsToLock.make = true;
          fieldsAutoFilled.make = true;
        }
        if (decoded.model) {
          fieldsToLock.model = true;
          fieldsAutoFilled.model = true;
        }
        if (decoded.trim) {
          fieldsToLock.trim = true;
          fieldsAutoFilled.trim = true;
        }
        
        // Update car data
        setCars(prev => {
          const newCars = [...prev];
          if (decoded.year) {
            newCars[index] = { ...newCars[index], year: String(decoded.year) };
          }
          if (decoded.make) {
            newCars[index] = { ...newCars[index], make: decoded.make };
          }
          if (decoded.model) {
            newCars[index] = { ...newCars[index], model: decoded.model };
          }
          if (decoded.trim) {
            newCars[index] = { ...newCars[index], trim: decoded.trim };
          }
          return newCars;
        });
        
        // Lock the auto-filled fields and set visual confirmation state
        if (Object.keys(fieldsToLock).length > 0) {
          setLockedFields(prev => ({ ...prev, [index]: fieldsToLock }));
          setAutoFilledFields(prev => ({ ...prev, [index]: fieldsAutoFilled }));
        }
      }
    } catch (error) {
      console.error('VIN decode error:', error);
      setVinDecodeResults(prev => ({ 
        ...prev, 
        [index]: { 
          success: false, 
          error: "Couldn't auto-fill from that VIN. You can still enter details manually."
        } 
      }));
    } finally {
      setDecodingVin(prev => ({ ...prev, [index]: false }));
    }
  };

  // Add another car
  const addCar = () => {
    if (visibleCarCount < 5) {
      if (cars.length <= visibleCarCount) {
        setCars(prev => [...prev, createEmptyCar()]);
      }
      setVisibleCarCount(prev => prev + 1);
    }
  };

  // Remove car
  const removeCar = (index) => {
    if (index > 0) {
      setCars(prev => prev.filter((_, i) => i !== index));
      setVisibleCarCount(prev => Math.max(1, prev - 1));
    }
  };

  // Check if car has any data
  const carHasData = (car) => {
    if (car.inputMode === 'link') {
      return car.listing_link;
    }
    return car.year || car.make || car.model || car.price || car.zip || car.mileage || car.trim || car.vin;
  };

  // Validate car based on input mode
  const validateCar = (car, index, isRequired) => {
    const carErrors = {};
    const hasAnyData = carHasData(car);
    
    // Skip validation if not required and empty
    if (!isRequired && !hasAnyData) return carErrors;
    
    if (car.inputMode === 'link') {
      // Link mode - only validate listing_link
      if (isRequired && !car.listing_link) {
        carErrors[`car${index}_listing_link`] = 'Listing link is required';
      }
    } else {
      // Manual mode - validate all required fields
      if (!car.year) carErrors[`car${index}_year`] = 'Year is required';
      else if (!/^\d{4}$/.test(car.year) || parseInt(car.year) < 1900 || parseInt(car.year) > new Date().getFullYear() + 1) {
        carErrors[`car${index}_year`] = 'Enter a valid year';
      }
      
      if (!car.make) carErrors[`car${index}_make`] = 'Make is required';
      if (!car.model) carErrors[`car${index}_model`] = 'Model is required';
      if (!car.price) carErrors[`car${index}_price`] = 'Price is required';
      else if (isNaN(parseFloat(car.price.replace(/[,$]/g, '')))) {
        carErrors[`car${index}_price`] = 'Enter a valid price';
      }
      
      if (!car.zip) carErrors[`car${index}_zip`] = 'Zip is required';
      else if (!/^\d{5}$/.test(car.zip)) {
        carErrors[`car${index}_zip`] = 'Enter 5-digit zip';
      }
      
      // Mileage required for used cars
      if (car.condition === 'used') {
        if (!car.mileage) carErrors[`car${index}_mileage`] = 'Mileage required for used cars';
        else if (isNaN(parseInt(car.mileage.replace(/[,]/g, '')))) {
          carErrors[`car${index}_mileage`] = 'Enter valid mileage';
        }
      }
    }
    
    return carErrors;
  };

  // Validate all cars
  const validateAllCars = () => {
    let allErrors = {};
    
    cars.slice(0, visibleCarCount).forEach((car, index) => {
      const isRequired = index === 0;
      const carErrors = validateCar(car, index, isRequired);
      allErrors = { ...allErrors, ...carErrors };
    });
    
    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  };

  // Check if form is valid (for button state) - no longer used for disabling
  const isFormValid = () => {
    const car1 = cars[0];
    if (!car1) return false;
    
    if (car1.inputMode === 'link') {
      return !!car1.listing_link;
    }
    
    const hasRequiredFields = car1.year && car1.make && car1.model && car1.price && car1.zip;
    const mileageValid = car1.condition === 'new' || car1.mileage;
    
    return hasRequiredFields && mileageValid;
  };

  // Check if a single car is valid (has all required fields filled)
  const isCarValid = (car) => {
    if (!car) return false;
    
    // For link mode, car is valid if listing_link is provided
    if (car.inputMode === 'link') {
      return !!car.listing_link;
    }
    
    // For manual mode, check required fields: year, make, model, condition, price, zip
    // Plus mileage if condition is "used"
    const hasRequiredFields = car.year && car.make && car.model && car.condition && car.price && car.zip;
    const mileageValid = car.condition === 'new' || car.mileage;
    
    return hasRequiredFields && mileageValid;
  };

  // Count how many valid cars are provided
  const countValidCars = () => {
    return cars.slice(0, visibleCarCount).filter(isCarValid).length;
  };

  // Build API payload
  const buildPayload = () => {
    const validCars = cars.slice(0, visibleCarCount).filter(carHasData).map(car => {
      if (car.inputMode === 'link') {
        // Link mode - only pass listing_link, other fields as null
        return {
          year: null,
          make: null,
          model: null,
          condition: null,
          price: null,
          zip: null,
          mileage: null,
          trim: null,
          vin: null,
          listing_link: car.listing_link || null
        };
      }
      // Manual mode
      return {
        year: car.year ? parseInt(car.year) : null,
        make: car.make || null,
        model: car.model || null,
        condition: car.condition || null,
        price: car.price ? parseFloat(car.price.replace(/[,$]/g, '')) : null,
        zip: car.zip || null,
        mileage: car.mileage ? parseInt(car.mileage.replace(/[,]/g, '')) : null,
        trim: car.trim || null,
        vin: car.vin || null,
        listing_link: null
      };
    });
    
    return { cars: validCars };
  };

  // Handle Analyze
  const handleAnalyze = async () => {
    // Reset states
    setApiError(null);
    setLimitReached(false);
    setNotEnoughCarsError(false);
    setResults(null);
    
    // Check if at least 2 valid cars are provided
    const validCarCount = countValidCars();
    if (validCarCount < 2) {
      setNotEnoughCarsError(true);
      return;
    }
    
    // Check auth (bypassed when DEV_AUTH_BYPASS is true)
    if (!DEV_AUTH_BYPASS && !isAuthenticated) {
      setShowAuthGate(true);
      return;
    }
    
    // Validate
    if (!validateAllCars()) {
      return;
    }
    
    // Refresh entitlements
    await fetchEntitlements();
    
    setIsLoading(true);
    
    try {
      const payload = buildPayload();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Call the batch endpoint
      const response = await fetch(`${backendUrl}/api/compare-cars/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      // Handle 402 - compare limit reached
      if (response.status === 402) {
        const data = await response.json().catch(() => ({}));
        if (data.code === 'compare_limit_reached') {
          setLimitReached(true);
          setIsLoading(false);
          return;
        }
      }
      
      // Handle other errors
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      // Store results from batch endpoint
      setResults(data);
      if (data.compare_reports_remaining !== undefined) {
        setReportsRemaining(data.compare_reports_remaining);
      }
      
    } catch (error) {
      console.error('Compare API error:', error);
      setApiError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthGate(false);
  };

  const handleGetQuotes = () => {
    navigate('/tools/otd-quote-request');
  };

  const handleBack = () => {
    if (isEmbedded && onBackProp) {
      onBackProp();
    } else {
      navigate(-1);
    }
  };

  const toggleAccordion = (key) => {
    setAccordions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Render input field with error
  const renderInput = (index, field, label, placeholder, type = 'text', required = false) => {
    const errorKey = `car${index}_${field}`;
    const hasError = errors[errorKey];
    
    return (
      <div className="flex-1">
        <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
          {label}{required && <span style={{ color: '#DC2626' }}> *</span>}
        </label>
        <input
          type={type}
          value={cars[index]?.[field] || ''}
          onChange={(e) => updateCarField(index, field, e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasError ? 'border-red-400' : ''}`}
          style={{ borderColor: hasError ? '#F87171' : '#E5E7EB', backgroundColor: '#FFFFFF' }}
        />
        {hasError && (
          <p className="text-xs mt-1" style={{ color: '#DC2626' }}>{errors[errorKey]}</p>
        )}
      </div>
    );
  };

  // Render car card
  const renderCarCard = (index) => {
    const car = cars[index] || createEmptyCar();
    const isRequired = index === 0;
    const carLockedFields = lockedFields[index] || {};
    const carAutoFilledFields = autoFilledFields[index] || {};
    const hasLockedFields = Object.keys(carLockedFields).length > 0;
    
    // Helper to check if a field is auto-filled and should show confirmed styling
    const isFieldAutoFilled = (field) => carAutoFilledFields[field] === true;
    
    // Subtle auto-filled indicator - renders BELOW the input
    const AutoFilledIndicator = ({ field }) => {
      if (!isFieldAutoFilled(field)) return null;
      return (
        <div className="mt-1 flex items-center gap-1 text-xs" style={{ color: 'rgba(5, 150, 105, 0.8)' }}>
          <Check className="h-3 w-3" />
          <span>Auto-filled</span>
        </div>
      );
    };
    
    return (
      <div 
        key={index}
        className="rounded-xl p-4 mb-4"
        style={{ 
          backgroundColor: '#F9FAFB', 
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        }}
        data-testid={`car-card-${index}`}
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold" style={{ color: '#001F3F' }}>
            Car {index + 1} {isRequired && <span className="font-normal text-xs" style={{ color: '#6B7280' }}>(required)</span>}
          </span>
          {index > 0 && (
            <button
              onClick={() => removeCar(index)}
              className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              style={{ color: '#6B7280' }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* VIN-first entry */}
        <div className="space-y-3">
          {/* VIN with Auto-fill button */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
              VIN (recommended)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={car.vin || ''}
                onChange={(e) => updateCarField(index, 'vin', e.target.value.toUpperCase())}
                placeholder="1HGCV1F34LA123456"
                className="flex-1 px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                data-testid={`car-${index}-vin`}
                maxLength={17}
              />
              <button
                onClick={() => decodeVin(index)}
                disabled={decodingVin[index]}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                style={{ 
                  backgroundColor: decodingVin[index] ? '#E5E7EB' : '#001F3F',
                  color: decodingVin[index] ? '#6B7280' : '#FFFFFF'
                }}
                data-testid={`car-${index}-autofill-btn`}
              >
                {decodingVin[index] ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Auto-fill'
                )}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
              Paste the VIN to auto-fill year/make/model (if available).
            </p>
          </div>
          
          {/* VIN decode result status */}
          {vinDecodeResults[index] && (
            <div 
              className="rounded-lg px-3 py-2 text-sm"
              style={{ 
                backgroundColor: vinDecodeResults[index].success ? 'rgba(240, 253, 244, 0.6)' : '#FEF2F2',
                border: `1px solid ${vinDecodeResults[index].success ? 'rgba(187, 247, 208, 0.5)' : '#FECACA'}`
              }}
              data-testid={`car-${index}-vin-result`}
            >
              {vinDecodeResults[index].success ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5" style={{ color: '#059669' }} />
                    <span style={{ color: '#047857', fontSize: '13px' }}>
                      Auto-filled from VIN
                    </span>
                    <span 
                      className="px-1.5 py-0.5 rounded text-xs"
                      style={{ 
                        backgroundColor: vinDecodeResults[index].confidence === 'high' ? 'rgba(220, 252, 231, 0.6)' : 
                                        vinDecodeResults[index].confidence === 'medium' ? 'rgba(254, 243, 199, 0.6)' : 'rgba(254, 226, 226, 0.6)',
                        color: vinDecodeResults[index].confidence === 'high' ? '#047857' : 
                               vinDecodeResults[index].confidence === 'medium' ? '#92400E' : '#991B1B'
                      }}
                    >
                      {vinDecodeResults[index].confidence}
                    </span>
                  </div>
                  {hasLockedFields && (
                    <button
                      onClick={() => unlockFields(index)}
                      className="text-xs font-medium underline"
                      style={{ color: '#3B82F6' }}
                    >
                      Edit
                    </button>
                  )}
                </div>
              ) : (
                <p className="font-medium" style={{ color: '#991B1B' }}>
                  {vinDecodeResults[index].error || "Couldn't auto-fill from that VIN."}
                </p>
              )}
            </div>
          )}
          
          {/* Row 1: Year, Make, Model */}
          <div className="grid grid-cols-3 gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                Year{isRequired && <span style={{ color: '#DC2626' }}> *</span>}
              </label>
              <input
                type="text"
                value={car.year || ''}
                onChange={(e) => updateCarField(index, 'year', e.target.value)}
                placeholder="2021"
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isFieldAutoFilled('year') ? 'font-semibold' : ''}`}
                style={{
                  borderColor: errors[`car${index}_year`] ? '#F87171' : '#E5E7EB',
                  backgroundColor: isFieldAutoFilled('year') ? 'rgba(236, 253, 245, 0.4)' : '#FFFFFF'
                }}
                data-testid={`car-${index}-year`}
              />
              {isFieldAutoFilled('year') && !errors[`car${index}_year`] && (
                <AutoFilledIndicator field="year" />
              )}
              {errors[`car${index}_year`] && (
                <p className="text-xs mt-1" style={{ color: '#DC2626' }}>{errors[`car${index}_year`]}</p>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                Make{isRequired && <span style={{ color: '#DC2626' }}> *</span>}
              </label>
              <input
                type="text"
                value={car.make || ''}
                onChange={(e) => updateCarField(index, 'make', e.target.value)}
                placeholder="Honda"
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isFieldAutoFilled('make') ? 'font-semibold' : ''}`}
                style={{
                  borderColor: errors[`car${index}_make`] ? '#F87171' : '#E5E7EB',
                  backgroundColor: isFieldAutoFilled('make') ? 'rgba(236, 253, 245, 0.4)' : '#FFFFFF'
                }}
                data-testid={`car-${index}-make`}
              />
              {isFieldAutoFilled('make') && !errors[`car${index}_make`] && (
                <AutoFilledIndicator field="make" />
              )}
              {errors[`car${index}_make`] && (
                <p className="text-xs mt-1" style={{ color: '#DC2626' }}>{errors[`car${index}_make`]}</p>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                Model{isRequired && <span style={{ color: '#DC2626' }}> *</span>}
              </label>
              <input
                type="text"
                value={car.model || ''}
                onChange={(e) => updateCarField(index, 'model', e.target.value)}
                placeholder="Accord"
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isFieldAutoFilled('model') ? 'font-semibold' : ''}`}
                style={{
                  borderColor: errors[`car${index}_model`] ? '#F87171' : '#E5E7EB',
                  backgroundColor: isFieldAutoFilled('model') ? 'rgba(236, 253, 245, 0.4)' : '#FFFFFF'
                }}
                data-testid={`car-${index}-model`}
              />
              {isFieldAutoFilled('model') && !errors[`car${index}_model`] && (
                <AutoFilledIndicator field="model" />
              )}
              {errors[`car${index}_model`] && (
                <p className="text-xs mt-1" style={{ color: '#DC2626' }}>{errors[`car${index}_model`]}</p>
              )}
            </div>
          </div>
          
          {/* Row 2: Condition, Price, Zip */}
          <div className="grid grid-cols-3 gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                Condition<span style={{ color: '#DC2626' }}> *</span>
              </label>
              <select
                value={car.condition}
                onChange={(e) => updateCarField(index, 'condition', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF', minHeight: '38px' }}
                data-testid={`car-${index}-condition`}
              >
                <option value="used">Used</option>
                <option value="new">New</option>
              </select>
            </div>
            {renderInput(index, 'price', 'Price', '25000', 'text', isRequired)}
            {renderInput(index, 'zip', 'Zip', '90210', 'text', isRequired)}
          </div>
          
          {/* Mileage - shown only for used cars */}
          {car.condition === 'used' && (
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                {renderInput(index, 'mileage', 'Mileage', '45000', 'text', true)}
              </div>
            </div>
          )}
          
          {/* Trim (optional) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                Trim (optional)
              </label>
              <input
                type="text"
                value={car.trim || ''}
                onChange={(e) => updateCarField(index, 'trim', e.target.value)}
                placeholder="EX-L"
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isFieldAutoFilled('trim') ? 'font-semibold' : ''}`}
                style={{
                  borderColor: '#E5E7EB',
                  backgroundColor: isFieldAutoFilled('trim') ? 'rgba(236, 253, 245, 0.4)' : '#FFFFFF'
                }}
                data-testid={`car-${index}-trim`}
              />
              {isFieldAutoFilled('trim') && (
                <AutoFilledIndicator field="trim" />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render accordion section
  const renderAccordion = (key, title, content) => {
    const isOpen = accordions[key];
    return (
      <div 
        className="rounded-xl overflow-hidden transition-shadow"
        style={{ 
          backgroundColor: '#FFFFFF', 
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
        }}
      >
        <button
          onClick={() => toggleAccordion(key)}
          className="w-full px-4 py-3.5 flex items-center justify-between text-left transition-colors hover:bg-gray-50"
          style={{ borderBottom: isOpen ? '1px solid #E5E7EB' : 'none' }}
        >
          <h2 className="text-sm font-bold" style={{ color: '#001F3F' }}>{title}</h2>
          {isOpen ? (
            <ChevronUp className="w-5 h-5" style={{ color: '#6B7280' }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: '#6B7280' }} />
          )}
        </button>
        {isOpen && (
          <div className="px-4 py-3">
            {content}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F8F9FB' }}>
      {/* Header */}
      <div 
        style={{ 
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E8EAED',
        }}
      >
        <div className="max-w-screen-sm mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
              style={{ color: '#0B1221' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <span className="text-sm font-semibold tracking-wide">
              <span style={{ color: '#001F3F' }}>VINdicated.</span>
              <span style={{ color: '#FF4136' }}>ai</span>
            </span>
          </div>
        </div>
      </div>
      
      {/* Hero Card */}
      <div style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E8EAED' }}>
        <div className="max-w-screen-sm mx-auto px-4 py-6">
          <div 
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              border: '1px solid rgba(0, 31, 63, 0.08)',
              boxShadow: '0 4px 16px rgba(0,31,63,0.06), 0 2px 6px rgba(0,0,0,0.02)',
              overflow: 'hidden',
            }}
          >
            <div className="p-6">
              {/* Two-column layout: Text left, Visual right */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                {/* Left column: Title + Description (NO icon tile) */}
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold tracking-tight" style={{ color: '#001F3F' }}>
                    Compare your shortlist
                  </h1>
                  <div className="mt-3 text-sm leading-relaxed" style={{ color: '#6B7280', lineHeight: '1.65' }}>
                    <p className="mb-2">Deciding between a few cars? Compare your shortlist side-by-side to see real trade-offs.</p>
                    <p>Not sure which SUV, truck, or sedan makes the most sense? Add a few options and we&apos;ll build a side by side snapshot - price fairness, safety, running costs, value retention, and reliability/repair risk - so you can see what looks strongest before you fall in love.</p>
                  </div>
                </div>
                
                {/* Right column: Hero Visual - Premium Classic Balance Scale */}
                <div 
                  className="flex-shrink-0 flex items-center justify-center sm:justify-end"
                  style={{
                    opacity: scaleSettled ? 1 : 0,
                    transition: 'opacity 500ms ease-out',
                  }}
                >
                  <svg 
                    width="160" 
                    height="130" 
                    viewBox="0 0 160 130" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ overflow: 'visible' }}
                  >
                    {/* Base/Stand */}
                    <ellipse cx="80" cy="122" rx="32" ry="6" fill="none" stroke="#001F3F" strokeWidth="2.5" />
                    
                    {/* Center pillar */}
                    <line x1="80" y1="122" x2="80" y2="28" stroke="#001F3F" strokeWidth="3" strokeLinecap="round" />
                    
                    {/* Decorative pillar detail (small circle at top) */}
                    <circle cx="80" cy="24" r="5" fill="none" stroke="#001F3F" strokeWidth="2.5" />
                    
                    {/* Crossbeam pivot point */}
                    <circle cx="80" cy="32" r="3" fill="#001F3F" />
                    
                    {/* Crossbeam */}
                    <line 
                      x1="18" 
                      y1="36" 
                      x2="142" 
                      y2="36" 
                      stroke="#001F3F" 
                      strokeWidth="3" 
                      strokeLinecap="round"
                    />
                    
                    {/* Left side chains (3 lines for realistic pan suspension) */}
                    <g 
                      style={{
                        transform: scaleSettled ? 'translateY(0px)' : 'translateY(-8px)',
                        transition: 'transform 750ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <line x1="28" y1="36" x2="20" y2="58" stroke="#001F3F" strokeWidth="2" strokeLinecap="round" />
                      <line x1="28" y1="36" x2="28" y2="58" stroke="#001F3F" strokeWidth="2" strokeLinecap="round" />
                      <line x1="28" y1="36" x2="36" y2="58" stroke="#001F3F" strokeWidth="2" strokeLinecap="round" />
                      
                      {/* Left pan (bowl shape) */}
                      <path 
                        d="M8 60 Q8 78, 28 78 Q48 78, 48 60" 
                        fill="none" 
                        stroke="#001F3F" 
                        strokeWidth="2.5" 
                        strokeLinecap="round"
                      />
                      {/* Pan rim */}
                      <line x1="8" y1="60" x2="48" y2="60" stroke="#001F3F" strokeWidth="2.5" strokeLinecap="round" />
                    </g>
                    
                    {/* Right side chains (3 lines for realistic pan suspension) */}
                    <g 
                      style={{
                        transform: scaleSettled ? 'translateY(0px)' : 'translateY(8px)',
                        transition: 'transform 750ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <line x1="132" y1="36" x2="124" y2="58" stroke="#001F3F" strokeWidth="2" strokeLinecap="round" />
                      <line x1="132" y1="36" x2="132" y2="58" stroke="#001F3F" strokeWidth="2" strokeLinecap="round" />
                      <line x1="132" y1="36" x2="140" y2="58" stroke="#001F3F" strokeWidth="2" strokeLinecap="round" />
                      
                      {/* Right pan (bowl shape) */}
                      <path 
                        d="M112 60 Q112 78, 132 78 Q152 78, 152 60" 
                        fill="none" 
                        stroke="#001F3F" 
                        strokeWidth="2.5" 
                        strokeLinecap="round"
                      />
                      {/* Pan rim */}
                      <line x1="112" y1="60" x2="152" y2="60" stroke="#001F3F" strokeWidth="2.5" strokeLinecap="round" />
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-sm mx-auto px-4 py-4 space-y-4">
        
        {/* Car Input Section */}
        <div 
          className="rounded-xl p-4" 
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
        >
          <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
            You can compare up to five cars. Car one and car two give the clearest side by side results, the others are optional.
          </p>

          {/* Helper info box */}
          <div 
            className="rounded-lg p-3 mb-4 flex items-start gap-2"
            style={{ backgroundColor: '#F0F9FF', border: '1px solid #DBEAFE' }}
          >
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#1D4ED8' }} />
            <p className="text-sm" style={{ color: '#1E40AF' }}>
              Enter the car details below — VIN is recommended for faster auto-fill. For used cars, mileage is required.
            </p>
          </div>

          {/* Car cards */}
          {Array.from({ length: visibleCarCount }).map((_, index) => renderCarCard(index))}
          
          {/* Add another car button */}
          {visibleCarCount < 5 && (
            <button
              onClick={addCar}
              className="w-full py-2.5 px-4 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: '#D1D5DB', color: '#6B7280', marginBottom: '8px' }}
            >
              <Plus className="w-4 h-4" />
              Add another car
            </button>
          )}
        </div>

        {/* Error/Limit callouts */}
        {apiError && (
          <div 
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
            <p className="text-sm" style={{ color: '#991B1B' }}>{apiError}</p>
          </div>
        )}
        
        {limitReached && (
          <div 
            className="rounded-xl p-4"
            style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#92400E' }} />
              <div>
                <p className="text-sm font-medium mb-2" style={{ color: '#92400E' }}>
                  Compare limit reached
                </p>
                <p className="text-sm mb-3" style={{ color: '#78350F' }}>
                  You&apos;ve used all your compare reports. Upgrade to get more.
                </p>
                {/* TODO: Navigate to upgrade/billing if route exists */}
                <button
                  onClick={() => navigate('/paywall')}
                  className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                  style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
                >
                  View upgrade options
                  <ArrowRight className="w-4 h-4" style={{ color: '#FF4136' }} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Next Steps Card - containing both CTAs */}
        <div 
          className="rounded-2xl p-5"
          style={{ 
            backgroundColor: '#F8FAFC', 
            boxShadow: '0 2px 8px rgba(0,31,63,0.05), 0 4px 16px rgba(0,31,63,0.03)',
            border: '1px solid #E2E8F0'
          }}
        >
          {/* Section title */}
          <p 
            className="text-xs font-semibold tracking-wider mb-4"
            style={{ color: '#64748B', letterSpacing: '0.05em' }}
          >
            NEXT STEPS
          </p>

          <div className="flex flex-col gap-3">
            {/* Not enough cars error */}
            {notEnoughCarsError && (
              <div 
                className="rounded-xl p-4 flex items-start gap-3"
                style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
                <p className="text-sm" style={{ color: '#991B1B' }}>Please enter at least two cars to compare.</p>
              </div>
            )}

            {/* Primary CTA - Analyze */}
            <button
              onClick={handleAnalyze}
              disabled={isLoading}
              data-testid="analyze-shortlist-btn"
              className="w-full py-4 px-5 rounded-xl transition-all shadow-md hover:opacity-90"
              style={{ 
                backgroundColor: '#001F3F', 
                color: '#FFFFFF', 
                minHeight: '76px'
              }}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="font-bold text-base">Analyzing...</span>
                    </>
                  ) : (
                    <span className="font-bold text-base">Analyze my shortlist</span>
                  )}
                </div>
                {!isLoading && (
                  <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: '#FF4136' }} strokeWidth={2} />
                )}
              </div>
              <p className="text-xs font-normal text-left mt-1.5" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                {!isAuthenticated ? (
                  'Free account includes 3 free compare reports.'
                ) : reportsRemaining !== null ? (
                  `You have ${reportsRemaining} compare reports remaining.`
                ) : reportsInfo && reportsInfo.remaining !== undefined && reportsInfo.total !== undefined ? (
                  reportsInfo.isPaid 
                    ? `You have ${reportsInfo.remaining} of ${reportsInfo.total} compare reports left this year.`
                    : `You have ${reportsInfo.remaining} of ${reportsInfo.total} free compare reports left.`
                ) : (
                  'Loading reports info...'
                )}
              </p>
            </button>

            {/* Secondary CTA - Out-the-door quotes */}
            <button
              onClick={handleGetQuotes}
              className="w-full py-4 px-5 rounded-xl transition-all hover:shadow-md"
              style={{ 
                backgroundColor: '#FFFFFF', 
                color: '#001F3F',
                border: '1px solid #E2E8F0',
                minHeight: '76px'
              }}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 flex-shrink-0" style={{ color: '#001F3F' }} strokeWidth={2} />
                  <span className="font-bold text-base">Get out the door quotes</span>
                </div>
                <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: '#FF4136' }} strokeWidth={2} />
              </div>
              <p className="text-xs font-normal text-left mt-1.5" style={{ color: '#6B7280' }}>
                Use our email template to ask dealers for real out the door prices.
              </p>
            </button>
          </div>
        </div>

        {/* Results Section */}
        {results && results.results && results.results.length > 0 && (
          <div className="space-y-3" data-testid="compare-results-section">
            <h2 className="text-lg font-bold" style={{ color: '#001F3F' }}>
              Results
            </h2>
            {results.results.map((batchResult, index) => (
              <BatchResultCard 
                key={index} 
                batchResult={batchResult} 
                index={index} 
                scoreSegments={scoreSegments} 
                getConfidenceStyles={getConfidenceStyles} 
              />
            ))}
          </div>
        )}

        {/* Score breakdown visual - always visible */}
        <div 
          className="rounded-xl p-5" 
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', marginTop: '24px' }}
        >
          <p 
            className="text-xs font-semibold tracking-wider mb-3"
            style={{ color: '#6B7280', letterSpacing: '0.05em' }}
          >
            SCORE BREAKDOWN, 100 POINTS TOTAL
          </p>
          
          {/* Horizontal pill bar - rounded edges */}
          <div className="w-full h-10 rounded-xl overflow-hidden flex shadow-inner">
            {scoreSegments.map((segment, index) => (
              <div 
                key={segment.id}
                className="h-full transition-all"
                style={{ 
                  width: `${segment.points}%`,
                  backgroundColor: segment.color,
                  borderRight: index < scoreSegments.length - 1 ? '2px solid #FFFFFF' : 'none'
                }}
              />
            ))}
          </div>
          
          {/* Two column legend with icons - increased spacing */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {scoreSegments.map((segment) => {
              const IconComponent = segment.icon;
              return (
                <div key={segment.id} className="flex items-start gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${segment.color}15` }}
                  >
                    <IconComponent 
                      className="w-4 h-4" 
                      style={{ color: segment.color }} 
                      strokeWidth={2} 
                    />
                  </div>
                  <div className="flex items-start gap-2 pt-1">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: segment.color }}
                    />
                    <div>
                      <span className="text-sm" style={{ color: '#374151' }}>
                        {segment.label}, up to {segment.points} points
                      </span>
                      {segment.sublabel && (
                        <span className="text-xs block mt-0.5" style={{ color: '#6B7280' }}>
                          {segment.sublabel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Explanatory paragraph */}
          <p className="mt-5 text-sm leading-relaxed" style={{ color: '#4B5563' }}>
            We rank your cars from highest score to lowest score. If two cars are close, open the breakdown to see which category is driving the difference.
          </p>
        </div>

        {/* Accordion sections */}
        <div className="space-y-3 mt-4">
          {renderAccordion('howWeRank', 'How we rank your cars', (
            <div className="space-y-3 text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              <p>
                We turn each car in your shortlist into a clear 0 to 100 score, so you can compare options fast and understand why one ranks higher than another. The score is built from price fairness, value retention, safety, reliability and repair risk, and running costs, using your inputs plus published data sources when available.
              </p>
              <p>
                If we do not have enough data to judge a car fairly, we show &quot;Not enough data&quot; instead of guessing.
              </p>
            </div>
          ))}
          
          {renderAccordion('whatWeLookAt', 'What we look at for you', (
            <div className="space-y-0">
              {categoryExplanations.map((category, index) => (
                <div key={category.title}>
                  <div className="py-2.5">
                    <h3 className="text-sm font-semibold mb-1" style={{ color: '#001F3F' }}>
                      {category.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                      {category.content}
                    </p>
                  </div>
                  {index < categoryExplanations.length - 1 && (
                    <div className="border-b" style={{ borderColor: '#F3F4F6' }} />
                  )}
                </div>
              ))}
            </div>
          ))}
          
          {renderAccordion('whereDataComesFrom', 'Where the data comes from', (
            <div className="space-y-3 text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              <p>
                We use a mix of your inputs, public vehicle datasets, and licensed or partner datasets if enabled for your account.
              </p>
              <p>
                This feature is educational only and not financial advice. All pricing and cost estimates can be wrong, and you should verify key details before you buy.
              </p>
            </div>
          ))}
        </div>

        {/* Auth Gate Modal */}
        <AuthGate 
          isOpen={showAuthGate} 
          onClose={() => setShowAuthGate(false)}
          onSuccess={handleAuthSuccess}
        />

        {/* Continue Button for embedded mode */}
        {isEmbedded && onContinue && (
          <div className="pt-4">
            <button
              onClick={onContinue}
              className="w-full py-4 px-6 font-semibold text-base transition-all duration-150 flex items-center justify-center gap-2 active:scale-[0.98] hover:opacity-90"
              style={{ 
                backgroundColor: '#001F3F', 
                color: '#FFFFFF',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,31,63,0.20)',
              }}
            >
              <span>Continue my journey</span>
              <ArrowRight className="w-5 h-5" style={{ color: '#FF4136' }} strokeWidth={2} />
            </button>
          </div>
        )}

        {/* Legal Note */}
        <p className="text-xs text-center leading-relaxed" style={{ color: '#9CA3AF' }}>
          Educational use only. We summarize information from trusted third party sources, and we do not provide original safety ratings, financial advice, or guarantees.
        </p>
      </div>
    </div>
  );
};

// BatchResultCard component to handle batch API response
const BatchResultCard = ({ batchResult, index, scoreSegments, getConfidenceStyles }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // Handle error case
  if (batchResult.error) {
    const input = batchResult.input || {};
    const carLabel = input.year && input.make && input.model 
      ? `${input.year} ${input.make} ${input.model}`
      : input.listing_link 
        ? 'Link entry'
        : `Car ${index + 1}`;
    
    return (
      <div 
        className="rounded-xl p-4"
        style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
        data-testid={`result-card-error-${index}`}
      >
        <div className="flex items-start gap-3">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
            style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}
          >
            {index + 1}
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-1" style={{ color: '#991B1B' }}>
              {carLabel}
            </h3>
            <p className="text-sm" style={{ color: '#991B1B' }}>
              {batchResult.error}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Handle success case
  const result = batchResult.result;
  if (!result || !result.car) {
    return null;
  }
  
  const car = result.car;
  const scores = result.scores || {};
  const comps = result.comps || {};
  
  // Check if we have a total score
  const totalScore = scores.total_score_0_100;
  const hasScore = totalScore !== null && totalScore !== undefined;
  
  // Map backend score keys to frontend segment IDs
  const scoreMapping = {
    'price_fairness': { key: 'price_fairness', maxPoints: 30 },
    'value_retention': { key: 'value_retention', maxPoints: 25 },
    'safety': { key: 'safety', maxPoints: 20 },
    'reliability_repair': { key: 'reliability_qos', maxPoints: 15 },
    'running_costs': { key: 'running_cost', maxPoints: 10 }
  };
  
  // Get score for a category
  const getCategoryScore = (segmentId) => {
    const mapping = scoreMapping[segmentId];
    if (!mapping) return { score: null, reason: null };
    
    const scoreData = scores[mapping.key];
    if (!scoreData) return { score: null, reason: null };
    
    // Find the score key (e.g., score_0_30, score_0_20, etc.)
    const scoreKey = Object.keys(scoreData).find(k => k.startsWith('score_'));
    const score = scoreKey ? scoreData[scoreKey] : null;
    const reason = scoreData.reason_if_null;
    
    return { score, reason, maxPoints: mapping.maxPoints };
  };
  
  // Determine confidence level
  const confidence = scores.confidence || 'low';
  const confidenceLabel = confidence.charAt(0).toUpperCase() + confidence.slice(1);
  
  return (
    <div 
      className="rounded-xl p-4"
      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
      data-testid={`result-card-${index}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
            style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
          >
            {index + 1}
          </div>
          <div>
            <h3 className="font-semibold text-sm" style={{ color: '#001F3F' }}>
              {car.year} {car.make} {car.model}
              {car.trim && <span className="font-normal"> {car.trim}</span>}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
              ${car.price?.toLocaleString()} • {car.condition === 'used' ? `${car.mileage?.toLocaleString()} mi` : 'New'}
            </p>
          </div>
        </div>
        
        {/* Score or Not enough data */}
        <div className="text-right">
          {hasScore ? (
            <div className="text-2xl font-bold" style={{ color: '#001F3F' }}>
              {Math.round(totalScore)}
              <span className="text-sm font-normal" style={{ color: '#6B7280' }}>/100</span>
            </div>
          ) : (
            <span className="text-sm font-medium" style={{ color: '#6B7280' }}>
              Limited data
            </span>
          )}
        </div>
      </div>
      
      {/* Confidence label */}
      <div className="flex items-center gap-2 mb-3">
        <span 
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={getConfidenceStyles(confidenceLabel)}
        >
          {confidenceLabel} confidence
        </span>
        <span className="text-xs" style={{ color: '#6B7280' }}>
          {comps.count || 0} comparable listings found
        </span>
      </div>
      
      {/* Category breakdown */}
      <div className="mb-3">
        <div className="grid grid-cols-5 gap-1 mb-2">
          {scoreSegments.map(segment => {
            const { score, maxPoints } = getCategoryScore(segment.id);
            const displayScore = score !== null ? Math.round(score) : 0;
            const percentage = score !== null ? (score / maxPoints) * 100 : 0;
            
            return (
              <div key={segment.id} className="text-center">
                <div 
                  className="h-2 rounded-full mb-1"
                  style={{ backgroundColor: '#E5E7EB' }}
                >
                  <div 
                    className="h-full rounded-full transition-all"
                    style={{ 
                      width: `${percentage}%`, 
                      backgroundColor: score !== null ? segment.color : '#D1D5DB'
                    }}
                  />
                </div>
                <span className="text-xs" style={{ color: score !== null ? '#374151' : '#9CA3AF' }}>
                  {score !== null ? `${displayScore}/${maxPoints}` : '—'}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2 text-xs" style={{ color: '#6B7280' }}>
          {scoreSegments.map(segment => (
            <span key={segment.id} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: segment.color }} />
              {segment.label.split(' ')[0]}
            </span>
          ))}
        </div>
      </div>
      
      {/* Show details toggle */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="text-xs flex items-center gap-1 mb-2"
        style={{ color: '#3B82F6' }}
      >
        <Info className="w-3 h-3" />
        {showDetails ? 'Hide details' : 'Show score details'}
      </button>
      
      {/* Score details */}
      {showDetails && (
        <div className="p-3 rounded-lg text-xs space-y-2" style={{ backgroundColor: '#F9FAFB' }}>
          {scoreSegments.map(segment => {
            const { score, reason, maxPoints } = getCategoryScore(segment.id);
            return (
              <div key={segment.id} className="flex justify-between items-center">
                <span style={{ color: '#374151' }}>{segment.label}</span>
                {score !== null ? (
                  <span className="font-medium" style={{ color: segment.color }}>
                    {Math.round(score)}/{maxPoints} pts
                  </span>
                ) : (
                  <span style={{ color: '#9CA3AF' }}>
                    Not available yet{reason ? ` (${reason.replace(/_/g, ' ')})` : ''}
                  </span>
                )}
              </div>
            );
          })}
          
          {/* Comps info */}
          {comps.matching_notes && comps.matching_notes.length > 0 && (
            <div className="pt-2 border-t" style={{ borderColor: '#E5E7EB' }}>
              <p className="font-medium mb-1" style={{ color: '#374151' }}>Matching notes:</p>
              <ul className="list-disc pl-4 space-y-0.5" style={{ color: '#6B7280' }}>
                {comps.matching_notes.map((note, i) => (
                  <li key={i}>{note.replace(/_/g, ' ')}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* Summary message */}
      {!hasScore && (
        <p className="text-sm mt-2" style={{ color: '#6B7280' }}>
          We don&apos;t have enough comparable data to generate a full score for this vehicle yet. Some categories may become available as we expand our data sources.
        </p>
      )}
    </div>
  );
};

// Legacy ResultCard component (keeping for backwards compatibility)
const ResultCard = ({ result, index, scoreSegments, getConfidenceStyles }) => {
  const [showReasons, setShowReasons] = useState(false);
  const hasScore = result.score_total !== null && result.score_total !== undefined;
  
  return (
    <div 
      className="rounded-xl p-4"
      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
            style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
          >
            {result.rank || index + 1}
          </div>
          <div>
            <h3 className="font-semibold text-sm" style={{ color: '#001F3F' }}>
              {result.year} {result.make} {result.model}
              {result.trim && <span className="font-normal"> {result.trim}</span>}
            </h3>
          </div>
        </div>
        
        {/* Score or Not enough data */}
        <div className="text-right">
          {hasScore ? (
            <div className="text-2xl font-bold" style={{ color: '#001F3F' }}>
              {result.score_total}
              <span className="text-sm font-normal" style={{ color: '#6B7280' }}>/100</span>
            </div>
          ) : (
            <span className="text-sm font-medium" style={{ color: '#6B7280' }}>
              Not enough data
            </span>
          )}
        </div>
      </div>
      
      {/* Confidence label */}
      <div className="flex items-center gap-2 mb-3">
        <span 
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={getConfidenceStyles(result.confidence_label)}
        >
          {result.confidence_label || 'Unknown'} confidence
        </span>
        {result.confidence_reasons && result.confidence_reasons.length > 0 && (
          <button
            onClick={() => setShowReasons(!showReasons)}
            className="text-xs flex items-center gap-1"
            style={{ color: '#6B7280' }}
          >
            <Info className="w-3 h-3" />
            Details
          </button>
        )}
      </div>
      
      {/* Confidence reasons */}
      {showReasons && result.confidence_reasons && (
        <div className="mb-3 p-2 rounded text-xs" style={{ backgroundColor: '#F9FAFB', color: '#4B5563' }}>
          <ul className="list-disc pl-4 space-y-1">
            {result.confidence_reasons.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Category breakdown */}
      {hasScore && result.category_points && (
        <div className="mb-3">
          <div className="grid grid-cols-5 gap-1 mb-2">
            {scoreSegments.map(segment => {
              const points = result.category_points[segment.id] || 0;
              const percentage = (points / segment.points) * 100;
              return (
                <div key={segment.id} className="text-center">
                  <div 
                    className="h-2 rounded-full mb-1"
                    style={{ backgroundColor: '#E5E7EB' }}
                  >
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${percentage}%`, 
                        backgroundColor: segment.color 
                      }}
                    />
                  </div>
                  <span className="text-xs" style={{ color: '#6B7280' }}>
                    {points}/{segment.points}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 text-xs" style={{ color: '#6B7280' }}>
            {scoreSegments.map(segment => (
              <span key={segment.id} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: segment.color }} />
                {segment.label.split(' ')[0]}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Explanation */}
      {result.explanation_plain && (
        <p className="text-sm" style={{ color: '#4B5563' }}>
          {result.explanation_plain}
        </p>
      )}
      
      {/* Not enough data message */}
      {!hasScore && (
        <p className="text-sm" style={{ color: '#6B7280' }}>
          We don&apos;t have enough comparable data to generate a reliable score for this vehicle. This can happen with rare models, very new listings, or incomplete information.
        </p>
      )}
    </div>
  );
};

export default CompareCars;
